import { ToolButton } from '@rcs/ui';
import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { messages } from '../i18n/ja';
import { useMovableWindow } from '../layout/movable';
import { PLAYBACK_RATES, type PlaybackController, type PlaybackRate } from '../playback/controller';

/**
 * 再生・一時停止・停止・再生速度の操作（FR-SIM-001, 002）。3D ビューの下端に重ねて置く。
 * `children`（走行中の操作など）は、2 段目に並べる。
 * 高さを親要素の CSS 変数 `--rcs-playback-height` に出し、上に重ねる表示（区間の表示など）がずれて置かれるようにする。
 */
export function PlaybackBar({
  controller,
  children,
}: {
  readonly controller: PlaybackController | undefined;
  readonly children?: ReactNode;
}) {
  const {
    attach: windowAttach,
    element: windowElement,
    style: windowStyle,
    handle: windowHandle,
  } = useMovableWindow<HTMLDivElement>('playback', messages.windows.names.playback);
  const bar = windowElement;
  useEffect(() => {
    const parent = bar?.parentElement;
    if (!bar || !parent || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      parent.style.setProperty('--rcs-playback-height', `${String(bar.offsetHeight)}px`);
    });
    observer.observe(bar);
    return () => {
      observer.disconnect();
      parent.style.removeProperty('--rcs-playback-height');
    };
  }, [bar]);
  const t = messages.playback;
  // コントローラーがないときも、フックの呼び出し順を保つため同じ形で購読する
  const snapshot = useSyncExternalStore(
    controller?.subscribe ?? noopSubscribe,
    controller?.getSnapshot ?? emptySnapshot,
  );
  const disabled = controller === undefined;
  const playLabel = snapshot.playing ? t.pause : t.play;
  return (
    <div
      ref={windowAttach}
      style={windowStyle}
      className="playback-bar"
      role="group"
      aria-label={t.group}
      data-testid="playback-bar"
    >
      {windowHandle}
      <ToolButton
        label={playLabel}
        icon={<span aria-hidden="true">{snapshot.playing ? '❚❚' : '▶'}</span>}
        disabled={disabled}
        onClick={() => {
          controller?.toggle();
        }}
        data-testid="playback-toggle"
      />
      <ToolButton
        label={t.stop}
        icon={<span aria-hidden="true">■</span>}
        disabled={disabled}
        onClick={() => {
          controller?.stop();
        }}
        data-testid="playback-stop"
      />
      <label className="playback-bar__rate">
        <span>{t.rate}</span>
        <select
          value={snapshot.rate}
          disabled={disabled}
          data-testid="playback-rate"
          onChange={(event) => {
            controller?.setRate(Number(event.target.value) as PlaybackRate);
          }}
        >
          {PLAYBACK_RATES.map((rate) => (
            <option key={rate} value={rate}>
              {`${String(rate)}×`}
            </option>
          ))}
        </select>
      </label>
      <span className="playback-bar__time" data-testid="playback-time" role="timer">
        {`${t.elapsed} ${snapshot.time.toFixed(1)} / ${snapshot.duration.toFixed(1)} s`}
        {snapshot.ended ? `（${t.ended}）` : ''}
      </span>
      {children}
    </div>
  );
}

function noopSubscribe(): () => void {
  return () => undefined;
}

const emptySnapshot = (() => {
  const snapshot = { time: 0, duration: 0, playing: false, rate: 1 as PlaybackRate, ended: false };
  return () => snapshot;
})();
